import { cn } from '@/lib/utils';

import type { RunState } from './api';

/**
 * The mark beside a conversation in the sidebar, and the words under its
 * title. One colour per meaning, the same everywhere:
 *
 *   turning   answering             (lighter while it waits its turn)
 *   orange    waiting for your choice
 *   blue      answer ready, not opened yet
 *   red       never finished
 *
 * English always: this is the system talking, not an answer.
 */

export const RUN_LABEL: Record<RunState, string> = {
  running: 'Answering…',
  queued: 'Waiting to start',
  needs_choice: 'Needs your choice',
  done: 'Answer ready',
  failed: "Didn't finish"
};

export const RUN_TEXT_CLASS: Record<RunState, string> = {
  running: 'text-muted-foreground',
  queued: 'text-muted-foreground',
  needs_choice: 'text-orange-600 dark:text-orange-400',
  done: 'text-blue-600 dark:text-blue-400',
  failed: 'text-red-600 dark:text-red-400'
};

export function RunMark({
  state,
  className
}: {
  state: RunState;
  className?: string;
}) {
  if (state === 'running' || state === 'queued') {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2',
          state === 'running'
            ? 'border-muted-foreground/25 border-t-muted-foreground'
            : 'border-muted-foreground/15 border-t-muted-foreground/45',
          className
        )}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-block h-2 w-2 shrink-0 rounded-full',
        state === 'needs_choice' && 'bg-orange-500',
        state === 'done' && 'bg-blue-600 dark:bg-blue-400',
        state === 'failed' && 'bg-red-600 dark:bg-red-400',
        className
      )}
    />
  );
}
